import type { RefObject } from 'react';

interface InsertNodeOptions {
  node: Node;
  inputRef: RefObject<HTMLDivElement>;
  onPayloadChange: (text: string) => void;
}

export const insertNode = ({ node, inputRef, onPayloadChange }: InsertNodeOptions) => {
  const selection = window.getSelection();
  const range = selection?.getRangeAt(0);

  if (!range || !inputRef.current) {
    inputRef.current?.appendChild(node);
    onPayloadChange(inputRef.current?.innerText || '');
    return;
  }

  let rangeNode: Node | null = range.commonAncestorContainer;

  while (rangeNode) {
    if (rangeNode === inputRef.current) {
      range.deleteContents();
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);

      onPayloadChange(inputRef.current.innerText);
      break;
    }
    rangeNode = rangeNode.parentNode;
  }

  if (!rangeNode) {
    inputRef.current.appendChild(node);
    onPayloadChange(inputRef.current.innerText);
  }
};

export const createImageNode = (src: string) => {
  const img = document.createElement('img');
  img.src = src;
  img.style.maxWidth = '200px';
  img.style.maxHeight = '200px';
  img.style.margin = '4px';
  img.style.verticalAlign = 'middle';
  return img;
};

export const handleImageFile = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

export const handlePaste = (ev: React.ClipboardEvent<HTMLDivElement>) => {
  ev.preventDefault();
  let text = ev.clipboardData.getData('text/plain');
  text = text.replace(/\r\n|\r|\n/g, '<br>');
  document.execCommand('insertHTML', false, text);
};

export const handleInput = (
  e: React.ChangeEvent<HTMLDivElement>,
  setPayload: (value: string) => void
) => {
  setPayload(e.target.innerText);
};

export const handleKeyDown = (
  e: React.KeyboardEvent<HTMLDivElement>,
  insertLineBreak: () => void,
  send: () => void
) => {
  switch (e.key) {
    case 'Enter':
      if (e.ctrlKey || e.shiftKey) {
        if (e.ctrlKey) {
          e.preventDefault();
          insertLineBreak();
        }
      } else {
        e.preventDefault();
        send();
      }
      break;
  }
};

export const handleFiles = async (
  files: File[],
  inputRef: RefObject<HTMLDivElement>,
  setPayload: (value: string) => void
) => {
  const imageFiles = files.filter((file) => file.type.startsWith('image/'));

  if (imageFiles.length > 0) {
    for (const file of imageFiles) {
      try {
        const src = await handleImageFile(file);
        const img = createImageNode(src);
        insertNode({
          node: img,
          inputRef,
          onPayloadChange: setPayload,
        });
      } catch (error) {
        console.error('Failed to handle image file:', error);
      }
    }
  }
};

export const focusAndMoveToEnd = (element: HTMLDivElement | null) => {
  if (!element) return;

  element.focus();

  if (!element.childNodes.length) return;

  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();

  const lastChild = element.lastChild;
  if (lastChild) {
    if (lastChild.nodeType === Node.TEXT_NODE) {
      range.setStart(lastChild, lastChild.textContent?.length || 0);
    } else {
      range.setStartAfter(lastChild);
    }
  } else {
    range.setStart(element, 0);
  }

  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};
